import {
  configureStore,
  ThunkAction,
  Action,
  AnyAction,
  ThunkDispatch
} from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import homeReducer from '../redux/homeSlice';
import settingsReducer from '../redux/settingsSlice';
import workReducer from '../redux/workSlice';
import timetableReducer from '../redux/timetableSlice';
import visualReducer from '../redux/visualSlice';
import judgesStatusReducer from '../redux/judgesStatusSlice';
import timerReducer from '../redux/timerSlice';
import warningsReducer from '../redux/warningsSlice';
import publicResultsReducer from '../redux/publicResultsSlice';
import breakingBracketReducer from '../redux/breakingBracketSlice';
import {
  persistReducer,
} from 'redux-persist';
import createIdbStorage from 'redux-persist-indexeddb-storage';

const storage = createIdbStorage({ name: 'marx-mobile', storeName: 'state' });

const persistConfig = {
  key: 'root',
  version: 8, // Bumped to drop the unused socketsSent log
  storage,
  // Without this every action serialises the whole work slice into IndexedDB.
  // Continuous input (the breaking pre-selection fader) queued writes faster
  // than the tablet could drain them and the UI fell seconds behind.
  throttle: 1000,
  whitelist: ['home', 'settings', 'work', 'timetable'],
  migrate: (state: any) => {
    // Migration for version 2: add new timetable selection fields
    if (state && state._persist?.version === 1) {
      return Promise.resolve({
        ...state,
        timetable: {
          ...state.timetable,
          completedHeats: state.timetable?.completedHeats || [],
          selectedParticipants: state.timetable?.selectedParticipants || {},
          warningParticipants: state.timetable?.warningParticipants || {},
          resultsPresentedRounds: [],
          roundSelectedParticipants: {},
          roundWarningParticipants: {},
          resultsPresentationSelected: {},
          manuallyCompletedTTItems: []
        },
        _persist: {
          ...state._persist,
          version: 6
        }
      });
    }
    // Migration for version 3: add resultsPresentedRounds field
    if (state && state._persist?.version === 2) {
      return Promise.resolve({
        ...state,
        timetable: {
          ...state.timetable,
          resultsPresentedRounds: state.timetable?.resultsPresentedRounds || [],
          roundSelectedParticipants: {},
          roundWarningParticipants: {},
          resultsPresentationSelected: {},
          manuallyCompletedTTItems: []
        },
        _persist: {
          ...state._persist,
          version: 6
        }
      });
    }
    // Migration for version 4: add round-level selection fields
    if (state && state._persist?.version === 3) {
      return Promise.resolve({
        ...state,
        timetable: {
          ...state.timetable,
          roundSelectedParticipants: state.timetable?.roundSelectedParticipants || {},
          roundWarningParticipants: state.timetable?.roundWarningParticipants || {},
          resultsPresentationSelected: {},
          manuallyCompletedTTItems: []
        },
        _persist: {
          ...state._persist,
          version: 6
        }
      });
    }
    // Migration for version 5: add resultsPresentationSelected field
    if (state && state._persist?.version === 4) {
      return Promise.resolve({
        ...state,
        timetable: {
          ...state.timetable,
          resultsPresentationSelected: state.timetable?.resultsPresentationSelected || {},
          manuallyCompletedTTItems: []
        },
        _persist: {
          ...state._persist,
          version: 6
        }
      });
    }
    // Migration for version 6: add manuallyCompletedTTItems field
    if (state && state._persist?.version === 5) {
      return Promise.resolve({
        ...state,
        timetable: {
          ...state.timetable,
          manuallyCompletedTTItems: state.timetable?.manuallyCompletedTTItems || []
        },
        _persist: {
          ...state._persist,
          version: 6
        }
      });
    }
    // Migration for version 7: storage backend change (localStorage → IndexedDB)
    // No state shape change needed, redux-persist handles rehydration from the new backend
    if (state && state._persist?.version === 6) {
      return Promise.resolve({
        ...state,
        _persist: {
          ...state._persist,
          version: 7
        }
      });
    }
    // Migration for version 8: drop socketsSent. It was an append-only,
    // persisted log of every socket element ever emitted that nothing ever
    // read; without this the accumulated array would rehydrate forever on
    // devices that have been in use, which is where the bloat actually is.
    if (state && state._persist?.version === 7) {
      const work = { ...(state.work || {}) };
      delete work.socketsSent;
      return Promise.resolve({
        ...state,
        work,
        _persist: {
          ...state._persist,
          version: 8
        }
      });
    }
    return Promise.resolve(state);
  }
};

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    home: homeReducer,
    settings: settingsReducer,
    work: workReducer,
    timetable: timetableReducer,
    visual: visualReducer, // Visual control state (LED/Broadcast)
    judgesStatus: judgesStatusReducer, // Judges status from main app
    timer: timerReducer, // Countdown timer state from main app (not persisted - always fresh from server)
    warnings: warningsReducer, // Chairperson warnings pushed from main app (not persisted - live only)
    publicResults: publicResultsReducer, // Online results address for the monitor QR (not persisted - live only)
    breakingBracket: breakingBracketReducer, // Breaking bracket pushed from the desktop (not persisted - live only)
  })
);
const reducerProxy = (state: any, action: AnyAction) => {
  if (action.type === 'CLEAR_ALL') {
    storage.removeItem('persist:root');
    // return persistedReducer(undefined, action);
    state = undefined;
  }
  return persistedReducer(state, action);
};
export const store = configureStore({
  reducer: reducerProxy,
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      // serializableCheck: {
      //   ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      // },
      // immutableCheck: false
      immutableCheck: false,
      serializableCheck: false,
    })
  ]
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunkDispatch = ThunkDispatch<RootState, any, AnyAction>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
